import Plyr from 'plyr';

const useKlaro = true; // Set to true to enable Klaro, false to bypass it

let player; // Define the player variable in the global scope

function isYouTubeVideo(url) {
    return url && url.match(/youtube\.com|youtu\.be/);
}

function isVimeoVideo(url) {
    return url && url.match(/vimeo\.com/);
}

function isMp4Video(url) {
    return url && url.match(/\.mp4$/);
}

function getServiceName(videoUrl) {
    if (isYouTubeVideo(videoUrl)) {
        return 'YouTube';
    }
    if (isVimeoVideo(videoUrl)) {
        return 'Vimeo';
    }
    return null;
}

function getVideoId(videoUrl, provider) {
    if (provider === 'youtube') {
        let videoId;
        if (videoUrl.includes('youtu.be')) {
            videoId = videoUrl.split('/').pop();
        } else {
            videoId =
                videoUrl.split('v=')[1]?.split('&')[0] ||
                videoUrl.split('/').pop();
        }
        videoId = videoId.split('?')[0]; // Ensure we only have the video ID
        return videoId;
    } else if (provider === 'vimeo') {
        return videoUrl.split('/').pop();
    }
    return null;
}

function createVideoElement(videoUrl) {
    let provider, videoId;
    if (isYouTubeVideo(videoUrl)) {
        provider = 'youtube';
        videoId = getVideoId(videoUrl, provider);
        const div = document.createElement('div');
        div.id = `player-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        div.setAttribute('data-plyr-provider', provider);
        div.setAttribute('data-plyr-embed-id', videoId);
        return div;
    } else if (isVimeoVideo(videoUrl)) {
        provider = 'vimeo';
        videoId = getVideoId(videoUrl, provider);
        const div = document.createElement('div');
        div.id = `player-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        div.setAttribute('data-plyr-provider', provider);
        div.setAttribute('data-plyr-embed-id', videoId);
        return div;
    } else if (isMp4Video(videoUrl)) {
        const video = document.createElement('video');
        video.id = `player-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        video.setAttribute('playsinline', '');
        const source = document.createElement('source');
        source.src = videoUrl;
        source.type = 'video/mp4';
        video.appendChild(source);
        return video;
    }

    return null;
}

function initializePlayer(videoUrl, modalID) {
    const modal = document.getElementById(modalID);
    const videoContainer = modal.querySelector('.video-container');

    videoContainer.innerHTML = '';

    const videoElement = createVideoElement(videoUrl);
    if (videoElement) {
        videoContainer.appendChild(videoElement);
        player = new Plyr(videoElement, {
            autoplay: true
        });
    }

    modal.classList.remove('hidden');
}

function klaroIsReady() {
    return new Promise((resolve) => {
        const checkKlaro = () => {
            if (window.klaroInitialized && window.klaro.getManager()) {
                resolve();
            } else {
                setTimeout(checkKlaro, 1000);
            }
        };

        if (document.readyState === 'complete') {
            checkKlaro();
        } else {
            document.addEventListener('DOMContentLoaded', checkKlaro);
            document.addEventListener('klaroInitialized', checkKlaro);
        }
    });
}

function hasConsent(service) {
    if (!useKlaro) {
        return Promise.resolve(true); // Bypass Klaro and assume consent
    }

    return klaroIsReady()
        .then(() => {
            const manager = window.klaro.getManager();
            const consents = manager.consents;
            return consents[service] === true;
        })
        .catch((error) => {
            console.error('Error checking consent:', error);
            return false;
        });
}

function setConsent(service, value) {
    if (!useKlaro) {
        return; // Skip setting consent if Klaro is bypassed
    }

    klaroIsReady()
        .then(() => {
            const manager = window.klaro.getManager();
            manager.updateConsent(service, value);
            return manager.saveAndApplyConsents();
        })
        .catch((error) => {
            console.error('Error setting consent:', error);
        });
}

function requestConsent(service, callback) {
    if (!useKlaro) {
        callback(); // Bypass Klaro and directly proceed to the callback
        return;
    }

    const customConsentPrompt = document.getElementById('customConsentPrompt');
    const acceptButton = document.getElementById('acceptConsent');
    const closeButton = document.getElementById('closeConsentPrompt');
    const consentDescription = document.getElementById('consentDescription');

    consentDescription.textContent = `Do you want to load external content supplied by ${service}?`;

    customConsentPrompt.classList.remove('hidden');

    const closeModal = () => {
        customConsentPrompt.classList.add('hidden');
        window.removeEventListener('click', handleWindowClick);
    };

    acceptButton.onclick = () => {
        setConsent(service, true);
        closeModal();
        callback();
    };

    closeButton.onclick = closeModal;

    const handleWindowClick = (event) => {
        if (event.target === customConsentPrompt) {
            closeModal();
        }
    };

    window.addEventListener('click', handleWindowClick);
}

function trapFocus(modal) {
    const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

    modal.addEventListener('keydown', function (e) {
        const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

        if (!isTabPressed) {
            return;
        }

        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus();
                e.preventDefault();
            }
        } else {
            // Tab
            if (document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                e.preventDefault();
            }
        }
    });
}

function setupModalAccessibility(modal, trigger) {
    const closeButton = modal.querySelector('.close-modal');

    // Close modal on escape key press
    modal.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
            closeModal(modal, trigger);
        }
    });

    // Trap focus inside modal
    trapFocus(modal);

    // Close modal on close button click
    closeButton.addEventListener('click', function () {
        closeModal(modal, trigger);
    });

    // Restore focus to the trigger element when closing the modal
    function closeModal(modal, trigger) {
        if (player) {
            player.destroy(); // Stop and destroy the Plyr instance
            player = null;
        }
        modal.classList.add('hidden');
        trigger.focus();
        modal.querySelector('.video-container').innerHTML = ''; // Remove video element
    }
}

export function setupVideoModalTriggers(selector, modalID) {
    const modal = document.getElementById(modalID);
    const header = document.querySelector('header');
    const closeButton = modal.querySelector('.close-modal');
    let currentTrigger = null;

    document.querySelectorAll(selector).forEach((trigger) => {
        trigger.addEventListener('click', function () {
            currentTrigger = this;
            const imgElement = this.querySelector('img');
            if (!imgElement) return;

            const videoUrl = imgElement.getAttribute('data-video-url');
            if (!videoUrl) return;

            const service = getServiceName(videoUrl);

            if (!service) {
                console.error('Unsupported video service');
                return;
            }

            hasConsent(service)
                .then((consentGiven) => {
                    if (consentGiven) {
                        initializePlayer(videoUrl, modalID);
                        modal.classList.remove('hidden');
                        if (header) header.style.display = 'none';

                        // Set up accessibility for the modal
                        setupModalAccessibility(modal, trigger);

                        // Set focus to the first focusable element in the modal
                        const firstFocusableElement = modal.querySelector(
                            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                        );
                        if (firstFocusableElement) {
                            firstFocusableElement.focus();
                        }
                    } else {
                        requestConsent(service, () => {
                            initializePlayer(videoUrl, modalID);
                            modal.classList.remove('hidden');
                            if (header) header.style.display = 'none';

                            // Set up accessibility for the modal
                            setupModalAccessibility(modal, trigger);

                            // Set focus to the first focusable element in the modal
                            const firstFocusableElement = modal.querySelector(
                                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                            );
                            if (firstFocusableElement) {
                                firstFocusableElement.focus();
                            }
                        });
                    }
                })
                .catch((error) => {
                    console.error('Error checking consent promise:', error);
                });
        });
    });

    if (closeButton) {
        closeButton.addEventListener('click', () => {
            modal.classList.add('hidden');
            if (header) header.style.display = '';
            if (player) {
                player.destroy();
            }
            if (currentTrigger) {
                currentTrigger.focus();
            }
            modal.querySelector('.video-container').innerHTML = '';
        });
    }
}
